import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Eye, Check, X, FileText, Clock, Users, IndianRupee, Calendar, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const getStatusBadge = (status) => {
  const variants = {
    pending: { variant: 'outline', className: 'border-amber-500 text-amber-600 bg-amber-50' },
    approved: { variant: 'outline', className: 'border-blue-500 text-blue-600 bg-blue-50' },
    rejected: { variant: 'outline', className: 'border-red-500 text-red-600 bg-red-50' },
    completed: { variant: 'outline', className: 'border-green-500 text-green-600 bg-green-50' }
  };
  const config = variants[status] || { variant: 'outline', className: '' };
  return (
    <Badge variant={config.variant} className={config.className}>
      {status?.toUpperCase()}
    </Badge>
  );
};

// Calculate hours between two times
const calculateHours = (fromTime, toTime) => {
  if (!fromTime || !toTime) return 0;
  try {
    const [fromH, fromM] = fromTime.split(':').map(Number);
    const [toH, toM] = toTime.split(':').map(Number);
    let fromMinutes = fromH * 60 + fromM;
    let toMinutes = toH * 60 + toM;
    
    // Handle overnight shift
    if (toMinutes < fromMinutes) {
      toMinutes += 24 * 60;
    }
    
    return Math.round((toMinutes - fromMinutes) / 60 * 100) / 100;
  } catch {
    return 0;
  }
};

export default function OTPermissionPage() {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  
  // Form states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Dropdown data
  const [shopfloorEmployees, setShopfloorEmployees] = useState([]);
  const [officeEmployees, setOfficeEmployees] = useState([]);
  const [units, setUnits] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  
  // Create form state
  const [formData, setFormData] = useState({
    unit_id: '',
    unit_name: '',
    ot_date: '',
    from_time: '18:00',
    to_time: '22:00',
    workers: [],
    store_incharge_id: '',
    store_incharge_name: '',
    security_person_id: '',
    security_person_name: '',
    sales_order_ids: [],
    sales_order_numbers: [],
    client_order_text: '',
    reason: ''
  });
  
  // Completion report state
  const [completionData, setCompletionData] = useState({
    actual_date: '',
    actual_from_time: '',
    actual_to_time: '',
    store_incharge_id: '',
    store_incharge_name: '',
    security_person_id: '',
    security_person_name: '',
    workers: [],
    work_summary: ''
  });

  const getToken = () => localStorage.getItem('token');

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await fetch(`${API}/api/ot-permissions?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPermissions(data);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      toast.error('Failed to load OT permissions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ot-permissions/summary`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [shopfloorRes, officeRes, unitsRes, soRes] = await Promise.all([
        fetch(`${API}/api/ot-permissions/employees/shopfloor`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        fetch(`${API}/api/ot-permissions/employees/office`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        fetch(`${API}/api/locations?status=active`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        fetch(`${API}/api/sales-orders?status=confirmed&status=in_production`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      
      if (shopfloorRes.ok) {
        const data = await shopfloorRes.json();
        setShopfloorEmployees(data);
      }
      if (officeRes.ok) {
        const data = await officeRes.json();
        setOfficeEmployees(data);
      }
      if (unitsRes.ok) {
        const data = await unitsRes.json();
        setUnits(Array.isArray(data) ? data : []);
      }
      if (soRes.ok) {
        const data = await soRes.json();
        setSalesOrders(Array.isArray(data) ? data : (data.sales_orders || []));
      }
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  }, []);

  useEffect(() => {
    fetchUser();
    fetchPermissions();
    fetchSummary();
    fetchDropdownData();
  }, [fetchUser, fetchPermissions, fetchSummary, fetchDropdownData]);

  useEffect(() => {
    fetchPermissions();
  }, [statusFilter, fetchPermissions]);

  const isAdmin = user?.is_owner || user?.is_super_admin;

  // Calculate estimated values for create form
  const estimatedHours = calculateHours(formData.from_time, formData.to_time);
  const workerCount = formData.workers.length;
  const totalManHours = Math.round(workerCount * estimatedHours * 100) / 100;
  const estimatedCost = formData.workers.reduce((sum, w) => sum + (w.ot_hourly_rate || 0) * estimatedHours, 0);

  const handleCreateSubmit = async () => {
    if (!formData.unit_id) {
      toast.error('Please select a unit');
      return;
    }
    if (!formData.ot_date) {
      toast.error('Please select OT date');
      return;
    }
    if (!formData.from_time || !formData.to_time) {
      toast.error('Please specify time range');
      return;
    }
    if (estimatedHours <= 0) {
      toast.error('To Time must be after From Time');
      return;
    }
    if (formData.workers.length === 0) {
      toast.error('Please select at least one worker');
      return;
    }
    if (!formData.store_incharge_id) {
      toast.error('Please select Store Incharge');
      return;
    }
    if (!formData.security_person_id) {
      toast.error('Please select Security Person');
      return;
    }
    if (!formData.reason.trim()) {
      toast.error('Please provide reason for OT');
      return;
    }

    try {
      const res = await fetch(`${API}/api/ot-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`OT Permission ${data.request_number} created successfully`);
        setShowCreateDialog(false);
        resetForm();
        fetchPermissions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create OT permission');
      }
    } catch (err) {
      toast.error('Failed to create OT permission');
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API}/api/ot-permissions/${id}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success('OT Permission approved');
        fetchPermissions();
        setShowViewDialog(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to approve');
      }
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide rejection reason');
      return;
    }
    try {
      const res = await fetch(`${API}/api/ot-permissions/${selectedPermission?.id}/reject`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ reason: rejectionReason })
      });
      if (res.ok) {
        toast.success('OT Permission rejected');
        setShowRejectDialog(false);
        setShowViewDialog(false);
        setRejectionReason('');
        fetchPermissions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reject');
      }
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this OT request?')) return;
    try {
      const res = await fetch(`${API}/api/ot-permissions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success('OT Permission cancelled');
        fetchPermissions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to cancel');
      }
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  const openCompletionReport = (permission) => {
    setSelectedPermission(permission);
    // Pre-fill from original application
    setCompletionData({
      actual_date: permission.ot_date,
      actual_from_time: permission.from_time,
      actual_to_time: permission.to_time,
      store_incharge_id: permission.store_incharge_id,
      store_incharge_name: permission.store_incharge_name,
      security_person_id: permission.security_person_id,
      security_person_name: permission.security_person_name,
      workers: permission.workers.map(w => ({
        employee_id: w.employee_id,
        employee_name: w.employee_name,
        present: true,
        actual_in_time: permission.from_time,
        actual_out_time: permission.to_time,
        actual_hours: calculateHours(permission.from_time, permission.to_time),
        ot_hourly_rate: w.ot_hourly_rate,
        cost: calculateHours(permission.from_time, permission.to_time) * (w.ot_hourly_rate || 0)
      })),
      work_summary: ''
    });
    setShowCompletionDialog(true);
  };

  const updateWorkerCompletion = (index, field, value) => {
    const newWorkers = [...completionData.workers];
    newWorkers[index] = { ...newWorkers[index], [field]: value };
    
    // If marking absent, clear times and set hours/cost to 0
    if (field === 'present' && !value) {
      newWorkers[index].actual_in_time = '';
      newWorkers[index].actual_out_time = '';
      newWorkers[index].actual_hours = 0;
      newWorkers[index].cost = 0;
    }
    
    // Recalculate hours and cost when times change
    if ((field === 'actual_in_time' || field === 'actual_out_time') && newWorkers[index].present) {
      const hours = calculateHours(newWorkers[index].actual_in_time, newWorkers[index].actual_out_time);
      newWorkers[index].actual_hours = hours;
      newWorkers[index].cost = Math.round(hours * (newWorkers[index].ot_hourly_rate || 0) * 100) / 100;
    }
    
    setCompletionData({ ...completionData, workers: newWorkers });
  };

  const handleCompletionSubmit = async () => {
    if (!completionData.work_summary.trim()) {
      toast.error('Please provide work summary');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/ot-permissions/${selectedPermission.id}/completion-report`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(completionData)
      });
      
      if (res.ok) {
        toast.success('Completion report submitted successfully');
        setShowCompletionDialog(false);
        fetchPermissions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit completion report');
      }
    } catch (err) {
      toast.error('Failed to submit completion report');
    }
  };

  const resetForm = () => {
    // Auto-fill unit from user if available
    const userUnit = user?.unit_id || '';
    const userUnitName = user?.unit_name || '';
    
    setFormData({
      unit_id: userUnit,
      unit_name: userUnitName,
      ot_date: '',
      from_time: '18:00',
      to_time: '22:00',
      workers: [],
      store_incharge_id: '',
      store_incharge_name: '',
      security_person_id: '',
      security_person_name: '',
      sales_order_ids: [],
      sales_order_numbers: [],
      client_order_text: '',
      reason: ''
    });
  };

  const openViewDialog = (permission) => {
    setSelectedPermission(permission);
    setShowViewDialog(true);
  };

  // Check if completion report can be submitted (approved and OT date has passed)
  const canSubmitCompletion = (permission) => {
    if (permission.status !== 'approved') return false;
    if (permission.completion_report?.submitted) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const otDate = new Date(permission.ot_date);
    otDate.setHours(0, 0, 0, 0);
    return otDate <= today;
  };

  // Calculate completion totals
  const completionTotals = {
    workersPresent: completionData.workers.filter(w => w.present).length,
    totalWorkers: completionData.workers.length,
    totalHours: completionData.workers.reduce((sum, w) => sum + (w.present ? w.actual_hours : 0), 0),
    totalCost: completionData.workers.reduce((sum, w) => sum + (w.present ? w.cost : 0), 0)
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="ot-permission-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Production OT Permission</h1>
            <p className="text-gray-500 text-sm">Manage overtime work requests and approvals</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSummary(!showSummary)}
              className="gap-1"
            >
              {showSummary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Summary
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchPermissions(); fetchSummary(); }}
              className="gap-1"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              className="gap-1"
              data-testid="create-ot-request-btn"
            >
              <Plus className="h-4 w-4" />
              New OT Request
            </Button>
          </div>
        </div>

        {/* Summary Section */}
        {showSummary && summary && (
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Monthly Summary - {summary.month}/{summary.year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{summary.totals?.total_requests || 0}</div>
                  <div className="text-xs text-gray-500">Total Requests</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{summary.totals?.approved || 0}</div>
                  <div className="text-xs text-gray-500">Approved</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600">{summary.totals?.pending || 0}</div>
                  <div className="text-xs text-gray-500">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{Math.round(summary.totals?.planned_man_hours || 0)}</div>
                  <div className="text-xs text-gray-500">Planned Hrs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{formatCurrency(summary.totals?.planned_cost)}</div>
                  <div className="text-xs text-gray-500">Planned Cost</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <Label>Status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Permissions List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : permissions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No OT permission requests found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">OT Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workers</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {permissions.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-blue-600">{p.request_number}</span>
                        </td>
                        <td className="px-4 py-3">{formatDate(p.ot_date)}</td>
                        <td className="px-4 py-3 text-sm">
                          {p.from_time} - {p.to_time}
                          <span className="text-gray-400 ml-1">({p.estimated_hours}h)</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-gray-400" />
                            {p.worker_count}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{formatCurrency(p.estimated_cost)}</td>
                        <td className="px-4 py-3 text-sm">{p.requested_by_name}</td>
                        <td className="px-4 py-3">{getStatusBadge(p.status)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewDialog(p)}
                              data-testid={`view-btn-${p.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {p.status === 'pending' && isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                  onClick={() => handleApprove(p.id)}
                                  data-testid={`approve-btn-${p.id}`}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => { setSelectedPermission(p); setShowRejectDialog(true); }}
                                  data-testid={`reject-btn-${p.id}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {canSubmitCompletion(p) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCompletionReport(p)}
                                className="text-xs"
                                data-testid={`completion-btn-${p.id}`}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Report
                              </Button>
                            )}
                            {p.status === 'pending' && p.requested_by_id === user?.user_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleCancel(p.id)}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create OT Permission Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Production OT Permission Request</DialogTitle>
              <DialogDescription>Submit a request for overtime production work</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unit *</Label>
                  <SearchableSelect
                    options={units.map(u => ({ value: u.id, label: u.name }))}
                    value={formData.unit_id}
                    onValueChange={(val) => {
                      const unit = units.find(u => u.id === val);
                      setFormData({ ...formData, unit_id: val, unit_name: unit?.name || '' });
                    }}
                    placeholder="Select unit"
                  />
                </div>
                <div>
                  <Label>OT Date *</Label>
                  <Input
                    type="date"
                    value={formData.ot_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setFormData({ ...formData, ot_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Time */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>From Time *</Label>
                  <Input
                    type="time"
                    value={formData.from_time}
                    onChange={(e) => setFormData({ ...formData, from_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label>To Time *</Label>
                  <Input
                    type="time"
                    value={formData.to_time}
                    onChange={(e) => setFormData({ ...formData, to_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Estimated Hours</Label>
                  <div className="h-10 flex items-center px-3 bg-gray-100 rounded-md text-sm font-medium">
                    {estimatedHours} hrs
                  </div>
                </div>
              </div>

              {/* Workers */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-base font-medium">Workers *</Label>
                  <span className="text-sm text-gray-500">Shopfloor Staff Only</span>
                </div>
                <SearchableSelect
                  options={shopfloorEmployees.map(e => ({
                    value: e.id,
                    label: `${e.name} (${e.employee_code || 'No Code'}) - ₹${e.ot_hourly_rate}/hr`
                  }))}
                  value=""
                  onValueChange={(val) => {
                    if (val && !formData.workers.find(w => w.employee_id === val)) {
                      const emp = shopfloorEmployees.find(e => e.id === val);
                      if (emp) {
                        setFormData({
                          ...formData,
                          workers: [...formData.workers, {
                            employee_id: emp.id,
                            employee_name: emp.name,
                            ot_hourly_rate: emp.ot_hourly_rate || 0
                          }]
                        });
                      }
                    }
                  }}
                  placeholder="Add worker..."
                />
                {formData.workers.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {formData.workers.map((w, idx) => (
                      <div key={w.employee_id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded">
                        <span className="text-sm">{w.employee_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">₹{w.ot_hourly_rate}/hr</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => setFormData({
                              ...formData,
                              workers: formData.workers.filter((_, i) => i !== idx)
                            })}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4 pt-2 border-t">
                  <div className="text-center">
                    <div className="text-lg font-bold">{workerCount}</div>
                    <div className="text-xs text-gray-500">Workers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold">{totalManHours}</div>
                    <div className="text-xs text-gray-500">Man-Hours</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{formatCurrency(estimatedCost)}</div>
                    <div className="text-xs text-gray-500">Est. Cost</div>
                  </div>
                </div>
              </div>

              {/* Responsible Persons */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Store Incharge *</Label>
                  <SearchableSelect
                    options={officeEmployees.map(e => ({ value: e.id, label: e.name }))}
                    value={formData.store_incharge_id}
                    onValueChange={(val) => {
                      const emp = officeEmployees.find(e => e.id === val);
                      setFormData({
                        ...formData,
                        store_incharge_id: val,
                        store_incharge_name: emp?.name || ''
                      });
                    }}
                    placeholder="Select store incharge"
                  />
                </div>
                <div>
                  <Label>Security Person *</Label>
                  <SearchableSelect
                    options={officeEmployees.map(e => ({ value: e.id, label: e.name }))}
                    value={formData.security_person_id}
                    onValueChange={(val) => {
                      const emp = officeEmployees.find(e => e.id === val);
                      setFormData({
                        ...formData,
                        security_person_id: val,
                        security_person_name: emp?.name || ''
                      });
                    }}
                    placeholder="Select security person"
                  />
                </div>
              </div>

              {/* Purpose */}
              <div className="space-y-3">
                <div>
                  <Label>Client Orders (Select from active SOs)</Label>
                  <SearchableSelect
                    options={salesOrders.map(so => ({
                      value: so.id,
                      label: `${so.so_number} - ${so.customer_name || so.client_name}`
                    }))}
                    value=""
                    onValueChange={(val) => {
                      if (val && !formData.sales_order_ids.includes(val)) {
                        const so = salesOrders.find(s => s.id === val);
                        if (so) {
                          setFormData({
                            ...formData,
                            sales_order_ids: [...formData.sales_order_ids, val],
                            sales_order_numbers: [...formData.sales_order_numbers, so.so_number]
                          });
                        }
                      }
                    }}
                    placeholder="Add sales order..."
                  />
                  {formData.sales_order_numbers.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.sales_order_numbers.map((soNum, idx) => (
                        <Badge key={soNum} variant="secondary" className="gap-1">
                          {soNum}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                sales_order_ids: formData.sales_order_ids.filter((_, i) => i !== idx),
                                sales_order_numbers: formData.sales_order_numbers.filter((_, i) => i !== idx)
                              });
                            }}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Additional Notes (for orders not in system)</Label>
                  <Input
                    value={formData.client_order_text}
                    onChange={(e) => setFormData({ ...formData, client_order_text: e.target.value })}
                    placeholder="Enter order details if not in system..."
                  />
                </div>
                <div>
                  <Label>Reason for OT *</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Explain why overtime is needed..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateSubmit} data-testid="submit-ot-request-btn">
                Submit for Approval
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>{selectedPermission?.request_number}</span>
                {selectedPermission && getStatusBadge(selectedPermission.status)}
              </DialogTitle>
            </DialogHeader>
            
            {selectedPermission && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Unit</Label>
                    <div className="font-medium">{selectedPermission.unit_name}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">OT Date</Label>
                    <div className="font-medium">{formatDate(selectedPermission.ot_date)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Time</Label>
                    <div className="font-medium">{selectedPermission.from_time} - {selectedPermission.to_time} ({selectedPermission.estimated_hours} hrs)</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Workers</Label>
                    <div className="font-medium">{selectedPermission.worker_count} workers</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Man-Hours</Label>
                    <div className="font-medium">{selectedPermission.total_man_hours} hrs</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Estimated Cost</Label>
                    <div className="font-medium text-green-600">{formatCurrency(selectedPermission.estimated_cost)}</div>
                  </div>
                </div>

                <div className="border rounded p-3">
                  <Label className="text-gray-500">Workers List</Label>
                  <div className="mt-2 space-y-1">
                    {selectedPermission.workers?.map((w) => (
                      <div key={w.employee_id} className="flex justify-between text-sm">
                        <span>{w.employee_name}</span>
                        <span className="text-gray-500">₹{w.ot_hourly_rate}/hr</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Store Incharge</Label>
                    <div>{selectedPermission.store_incharge_name}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Security Person</Label>
                    <div>{selectedPermission.security_person_name}</div>
                  </div>
                </div>

                {selectedPermission.sales_order_numbers?.length > 0 && (
                  <div>
                    <Label className="text-gray-500">Client Orders</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedPermission.sales_order_numbers.map((so) => (
                        <Badge key={so} variant="outline">{so}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedPermission.client_order_text && (
                  <div>
                    <Label className="text-gray-500">Additional Notes</Label>
                    <div className="text-sm">{selectedPermission.client_order_text}</div>
                  </div>
                )}

                <div>
                  <Label className="text-gray-500">Reason for OT</Label>
                  <div className="text-sm whitespace-pre-wrap bg-gray-50 p-2 rounded">{selectedPermission.reason}</div>
                </div>

                {selectedPermission.status === 'rejected' && (
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <Label className="text-red-600">Rejection Reason</Label>
                    <div className="text-sm text-red-700">{selectedPermission.rejection_reason}</div>
                  </div>
                )}

                {selectedPermission.completion_report?.submitted && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Completion Report
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500">Actual Date</Label>
                        <div>{formatDate(selectedPermission.completion_report.actual_date)}</div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Workers Present</Label>
                        <div>{selectedPermission.completion_report.total_workers_present}</div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Actual Cost</Label>
                        <div className="font-medium text-green-600">{formatCurrency(selectedPermission.completion_report.total_ot_cost)}</div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-500">Work Summary</Label>
                      <div className="text-sm whitespace-pre-wrap bg-gray-50 p-2 rounded">{selectedPermission.completion_report.work_summary}</div>
                    </div>
                  </div>
                )}

                {selectedPermission.status === 'pending' && isAdmin && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button onClick={() => handleApprove(selectedPermission.id)} className="flex-1 bg-green-600 hover:bg-green-700">
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => setShowRejectDialog(true)}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject OT Permission</DialogTitle>
              <DialogDescription>Please provide a reason for rejection</DialogDescription>
            </DialogHeader>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectionReason(''); }}>Cancel</Button>
              <Button variant="destructive" onClick={handleReject}>Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Completion Report Dialog */}
        <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Post-OT Completion Report</DialogTitle>
              <DialogDescription>
                {selectedPermission?.request_number} | {formatDate(selectedPermission?.ot_date)}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Actual Times */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Actual Date</Label>
                  <Input
                    type="date"
                    value={completionData.actual_date}
                    onChange={(e) => setCompletionData({ ...completionData, actual_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Actual From Time</Label>
                  <Input
                    type="time"
                    value={completionData.actual_from_time}
                    onChange={(e) => setCompletionData({ ...completionData, actual_from_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Actual To Time</Label>
                  <Input
                    type="time"
                    value={completionData.actual_to_time}
                    onChange={(e) => setCompletionData({ ...completionData, actual_to_time: e.target.value })}
                  />
                </div>
              </div>

              {/* Responsible Persons */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Store Incharge</Label>
                  <SearchableSelect
                    options={officeEmployees.map(e => ({ value: e.id, label: e.name }))}
                    value={completionData.store_incharge_id}
                    onValueChange={(val) => {
                      const emp = officeEmployees.find(e => e.id === val);
                      setCompletionData({
                        ...completionData,
                        store_incharge_id: val,
                        store_incharge_name: emp?.name || ''
                      });
                    }}
                    placeholder="Select store incharge"
                  />
                </div>
                <div>
                  <Label>Security Person</Label>
                  <SearchableSelect
                    options={officeEmployees.map(e => ({ value: e.id, label: e.name }))}
                    value={completionData.security_person_id}
                    onValueChange={(val) => {
                      const emp = officeEmployees.find(e => e.id === val);
                      setCompletionData({
                        ...completionData,
                        security_person_id: val,
                        security_person_name: emp?.name || ''
                      });
                    }}
                    placeholder="Select security person"
                  />
                </div>
              </div>

              {/* Workers Attendance Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-center">Present</th>
                      <th className="px-3 py-2 text-center">In Time</th>
                      <th className="px-3 py-2 text-center">Out Time</th>
                      <th className="px-3 py-2 text-right">Hours</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {completionData.workers.map((w, idx) => (
                      <tr key={w.employee_id} className={!w.present ? 'bg-gray-50 text-gray-400' : ''}>
                        <td className="px-3 py-2">{w.employee_name}</td>
                        <td className="px-3 py-2 text-center">
                          <Checkbox
                            checked={w.present}
                            onCheckedChange={(checked) => updateWorkerCompletion(idx, 'present', checked)}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input
                            type="time"
                            value={w.actual_in_time}
                            onChange={(e) => updateWorkerCompletion(idx, 'actual_in_time', e.target.value)}
                            disabled={!w.present}
                            className="w-24 h-8 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input
                            type="time"
                            value={w.actual_out_time}
                            onChange={(e) => updateWorkerCompletion(idx, 'actual_out_time', e.target.value)}
                            disabled={!w.present}
                            className="w-24 h-8 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">{w.actual_hours}</td>
                        <td className="px-3 py-2 text-right font-medium">{formatCurrency(w.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-medium">
                    <tr>
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2 text-center">{completionTotals.workersPresent}/{completionTotals.totalWorkers}</td>
                      <td className="px-3 py-2" colSpan={2}></td>
                      <td className="px-3 py-2 text-right">{Math.round(completionTotals.totalHours * 100) / 100}</td>
                      <td className="px-3 py-2 text-right text-green-600">{formatCurrency(completionTotals.totalCost)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Work Summary */}
              <div>
                <Label>Work Summary *</Label>
                <Textarea
                  value={completionData.work_summary}
                  onChange={(e) => setCompletionData({ ...completionData, work_summary: e.target.value })}
                  placeholder="Describe what was accomplished during this OT shift..."
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompletionDialog(false)}>Cancel</Button>
              <Button onClick={handleCompletionSubmit} data-testid="submit-completion-btn">
                Submit Completion Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
